import React, { useState, useEffect, useMemo, useRef } from 'react';
import { searchSpecialities, Speciality } from '../../../utils/apiUtils';
import './SpecialityDialog.scss';


interface SpecialityDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (speciality: string) => void;
  specialities?: Speciality[];
}

const SpecialityDialog: React.FC<SpecialityDialogProps> = ({
  isOpen,
  onClose,
  onSelect,
  specialities
}) => {
  const [internalSpecialities, setInternalSpecialities] = useState<Speciality[]>(specialities || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSpeciality, setSelectedSpeciality] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredSpecialities = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return internalSpecialities;
    return internalSpecialities.filter((s) => {
      const label = (s.label ?? '').toLowerCase();
      const name = (s.name ?? '').toLowerCase();
      return label.includes(q) || name.includes(q);
    });
  }, [internalSpecialities, searchQuery]);

  useEffect(() => {
    if (isOpen) {
      setSelectedSpeciality('');
      setSearchQuery('');
      if (specialities && specialities.length > 0) {
        setInternalSpecialities(specialities);
        setLoading(false);
        setError(null);
      } else {
        fetchSpecialities();
      }
    }
  }, [isOpen, specialities]);

  useEffect(() => {
    if (isOpen && !loading && internalSpecialities.length > 0) {
      const id = window.requestAnimationFrame(() => searchInputRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
  }, [isOpen, loading, internalSpecialities.length]);

  const fetchSpecialities = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await searchSpecialities();
      setInternalSpecialities(data);
    } catch (error) {
      console.error('Ошибка при получении специальностей:', error);
      setError('Ошибка при загрузке специальностей');
      setInternalSpecialities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedSpeciality) {
      onSelect(selectedSpeciality);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedSpeciality('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="speciality-dialog-overlay">
      <div className="speciality-dialog">
        <div className="speciality-dialog-header">
          <h3>Выберите шаблон</h3>
          <button className="close-button" onClick={handleCancel}>
            ×
          </button>
        </div>

        <div className="speciality-dialog-content">
          {loading ? (
            <div className="loading">
              <div className="loader-dots">Загрузка специальностей...</div>
            </div>
          ) : error ? (
            <div className="error-message">
              {error}
            </div>
          ) : internalSpecialities.length === 0 ? (
            <div className="no-results">
              Специальности не найдены
            </div>
          ) : (
            <>
              <div className="speciality-dialog-search">
                <input
                  ref={searchInputRef}
                  type="search"
                  className="speciality-dialog-search-input"
                  placeholder="Поиск шаблона..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Поиск по списку шаблонов"
                />
              </div>
              <div className="specialities-list-scroll">
                {filteredSpecialities.length === 0 ? (
                  <div className="no-filter-results">Ничего не найдено</div>
                ) : (
                  <div className="specialities-list">
                    {filteredSpecialities.map((speciality, index) => (
                      <div
                        key={`${speciality.name}-${index}`}
                        className={`speciality-item ${selectedSpeciality === speciality.name ? 'selected' : ''}`}
                        onClick={() => setSelectedSpeciality(speciality.name)}
                      >
                        {speciality.label || speciality.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="speciality-dialog-footer">
          <button
            className="btn btn-secondary"
            onClick={handleCancel}
          >
            Отмена
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={!selectedSpeciality}
          >
            Выбрать
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpecialityDialog; 